import { useState, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Heart, ArrowLeft, Camera, Users, MessageSquare, Palette,
  Eye, Send, Save, Loader2, ChevronDown, ChevronUp,
  ImagePlus, X, Check, Lightbulb,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { useAnnouncementWizard } from '@/hooks/useAnnouncementWizard';
import { usePostPublishSuccess } from '@/hooks/usePostPublishSuccess';
import { useEventTracking } from '@/hooks/useEventTracking';
import { LivePreviewCard } from '@/components/mobile/LivePreviewCard';
import { ShareAnnouncementModal } from '@/components/sharing/ShareAnnouncementModal';
import { toast } from 'sonner';
import { RELATIONSHIP_OPTIONS, type RelationshipType, type AnnouncementFormData } from '@/types/announcement';
import type { TemplateType } from '@/types/templates';

// ─── Guided prompts by template ───
const GUIDED_PROMPTS: Record<string, string[]> = {
  apology: [
    "What happened that you want to address?",
    "How did it affect them?",
    "What do you wish you had done differently?",
  ],
  gratitude: [
    "What's something they did that changed your life?",
    "What memory with them do you treasure most?",
    "What would you want them to know right now?",
  ],
  hard_conversation: [
    "What's the hardest part to say?",
    "Why is now the right time?",
    "What do you hope they understand?",
  ],
  love: [
    "When did you first realize how much they mean to you?",
    "What do you admire most about them?",
    "What feeling do you want them to hold onto?",
  ],
  closure: [
    "What have you been holding onto?",
    "What do you need to let go of?",
    "What do you wish for them moving forward?",
  ],
  custom: [
    "What's on your heart right now?",
    "Why does this person matter to you?",
    "What would you regret not saying?",
  ],
};

const TEMPLATE_OPTIONS: { value: TemplateType; label: string; emoji: string }[] = [
  { value: 'gratitude', label: 'Thank You', emoji: '🙏' },
  { value: 'love', label: 'Love', emoji: '❤️' },
  { value: 'apology', label: 'Apology', emoji: '🕊️' },
  { value: 'hard_conversation', label: 'Hard Talk', emoji: '💬' },
  { value: 'closure', label: 'Closure', emoji: '🌅' },
  { value: 'custom', label: 'Custom', emoji: '✨' },
];

export default function MobileAnnouncementCreator() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [showPreview, setShowPreview] = useState(!isMobile);
  const [activePromptIndex, setActivePromptIndex] = useState(0);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [publishedAnnouncement, setPublishedAnnouncement] = useState<{
    id: string;
    public_slug: string;
    recipient_name: string;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { triggerSuccess } = usePostPublishSuccess();
  const { trackEvent } = useEventTracking();

  const {
    formData,
    isSubmitting,
    existingPhotoUrls,
    updateFormData,
    saveAsDraft,
    publishAnnouncement,
    removeExistingPhoto,
  } = useAnnouncementWizard();

  const prompts = GUIDED_PROMPTS[formData.template] || GUIDED_PROMPTS.custom;

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      if (files.length === 0) return;
      const remainingSlots = 3 - existingPhotoUrls.length;
      const newFiles = [...formData.photoFiles, ...files].slice(0, remainingSlots);
      const currentPreviewUrls = formData.photoUrls.slice(existingPhotoUrls.length);
      const newUrls = newFiles.map((file, index) =>
        formData.photoFiles[index] === file && currentPreviewUrls[index]
          ? currentPreviewUrls[index]
          : URL.createObjectURL(file)
      );
      updateFormData({ photoFiles: newFiles, photoUrls: [...existingPhotoUrls, ...newUrls] });
      e.target.value = '';
    },
    [formData.photoFiles, formData.photoUrls, updateFormData, existingPhotoUrls]
  );

  const removePhoto = useCallback(
    (index: number) => {
      if (index < existingPhotoUrls.length) {
        removeExistingPhoto(index);
      } else {
        const newIndex = index - existingPhotoUrls.length;
        const newFiles = formData.photoFiles.filter((_, i) => i !== newIndex);
        const urlIndex = existingPhotoUrls.length + newIndex;
        if (formData.photoUrls[urlIndex]) URL.revokeObjectURL(formData.photoUrls[urlIndex]);
        const newUrls = formData.photoUrls.slice(existingPhotoUrls.length).filter((_, i) => i !== newIndex);
        updateFormData({ photoFiles: newFiles, photoUrls: [...existingPhotoUrls, ...newUrls] });
      }
    },
    [formData, updateFormData, existingPhotoUrls, removeExistingPhoto]
  );

  const openPhotoPicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const allPhotoUrls = [...existingPhotoUrls, ...formData.photoUrls.slice(existingPhotoUrls.length)];

  const handlePublish = async () => {
    if (!formData.recipientName.trim()) {
      toast.error('Please enter the recipient name');
      return;
    }
    if (!formData.relationship) {
      toast.error('Please select a relationship');
      return;
    }
    if (!formData.message.trim()) {
      toast.error('Please write a message');
      return;
    }

    const result = await publishAnnouncement();
    if (!result) return;

    if ('needsCredits' in result && result.needsCredits) {
      toast.error('You need credits to publish. Visit Add Credits.');
      return;
    }

    if ('public_slug' in result && result.public_slug) {
      setPublishedAnnouncement({
        id: (result as any).id || '',
        public_slug: result.public_slug,
        recipient_name: result.recipient_name,
      });
      triggerSuccess('publish', result.public_slug, result.recipient_name);
      trackEvent('mobile_creator_published', { template: formData.template });
      setShareDialogOpen(true);
    }
  };

  const handleSaveDraft = async () => {
    if (!formData.relationship) {
      toast.error('Please select a relationship first');
      return;
    }
    const result = await saveAsDraft();
    if (result) navigate('/dashboard');
  };

  const handleShareClose = (open: boolean) => {
    setShareDialogOpen(open);
    if (!open) window.location.href = '/dashboard#puzzle';
  };

  return (
    <div className="min-h-screen bg-hero-gradient">
      {/* Header */}
      <header className="glass border-b border-border/20 sticky top-0 z-30 pt-[env(safe-area-inset-top)]">
        <div className="flex h-14 items-center justify-between px-4 max-w-7xl mx-auto">
          <Link to="/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm hidden sm:inline">Dashboard</span>
          </Link>
          <div className="flex items-center gap-2">
            <Heart className="h-5 w-5 text-primary" fill="currentColor" />
            <span className="font-heading text-lg font-bold text-foreground">Create</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={handleSaveDraft} disabled={isSubmitting} className="text-xs">
              <Save className="h-3.5 w-3.5 mr-1" />
              <span className="hidden sm:inline">Save</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Share Dialog */}
      {publishedAnnouncement && (
        <ShareAnnouncementModal
          open={shareDialogOpen}
          onOpenChange={handleShareClose}
          announcementId={publishedAnnouncement.id}
          publicSlug={publishedAnnouncement.public_slug}
          recipientName={publishedAnnouncement.recipient_name}
          message={formData.message}
          template={formData.template}
          relationship={formData.relationship}
          source="dashboard_card"
        />
      )}

      {/* Main content: side-by-side on desktop, stacked on mobile */}
      <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-6 p-4 lg:p-8">
        {/* LEFT: Form */}
        <div className="flex-1 space-y-6 lg:max-w-xl">
          {/* Section 1: Who */}
          <section className="glass rounded-2xl p-5 space-y-4 shadow-soft">
            <div className="flex items-center gap-2 text-primary mb-1">
              <Users className="h-4 w-4" />
              <h2 className="font-heading text-base font-semibold">Who is this for?</h2>
            </div>
            <Input
              placeholder="Their name (e.g., Mom, Grandma Rose…)"
              value={formData.recipientName}
              onChange={(e) => updateFormData({ recipientName: e.target.value })}
              className="text-base"
            />
            <div className="grid grid-cols-5 gap-2">
              {RELATIONSHIP_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => updateFormData({ relationship: opt.value })}
                  className={cn(
                    'flex flex-col items-center gap-1 p-2 rounded-xl border transition-all text-center',
                    formData.relationship === opt.value
                      ? 'border-primary bg-primary/10 shadow-sm'
                      : 'border-border/50 hover:border-primary/40'
                  )}
                >
                  <span className="text-lg">{opt.emoji}</span>
                  <span className="text-[10px] leading-tight text-muted-foreground">{opt.label}</span>
                </button>
              ))}
            </div>
          </section>

          {/* Section 2: Emotional Intent */}
          <section className="glass rounded-2xl p-5 space-y-4 shadow-soft">
            <div className="flex items-center gap-2 text-primary mb-1">
              <Palette className="h-4 w-4" />
              <h2 className="font-heading text-base font-semibold">What do you need to say?</h2>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {TEMPLATE_OPTIONS.map((t) => (
                <button
                  key={t.value}
                  onClick={() => {
                    updateFormData({ template: t.value });
                    setActivePromptIndex(0);
                  }}
                  className={cn(
                    'flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm font-medium transition-all',
                    formData.template === t.value
                      ? 'border-primary bg-primary/10 text-primary shadow-sm'
                      : 'border-border/50 text-muted-foreground hover:border-primary/40'
                  )}
                >
                  <span>{t.emoji}</span>
                  <span className="truncate">{t.label}</span>
                </button>
              ))}
            </div>
          </section>

          {/* Section 3: Write */}
          <section className="glass rounded-2xl p-5 space-y-4 shadow-soft">
            <div className="flex items-center gap-2 text-primary mb-1">
              <MessageSquare className="h-4 w-4" />
              <h2 className="font-heading text-base font-semibold">Write your message</h2>
            </div>

            {/* Guided prompts */}
            <div className="space-y-2">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Lightbulb className="h-3 w-3" />
                <span>Let this guide you:</span>
              </div>
              <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1">
                {prompts.map((prompt, i) => (
                  <button
                    key={i}
                    onClick={() => setActivePromptIndex(i)}
                    className={cn(
                      'flex-shrink-0 px-3 py-1.5 rounded-full text-xs border transition-all',
                      activePromptIndex === i
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border/50 text-muted-foreground hover:border-primary/30'
                    )}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
              <p className="text-sm text-foreground italic bg-muted/50 rounded-lg px-3 py-2">
                "{prompts[activePromptIndex]}"
              </p>
            </div>

            <Textarea
              placeholder="Write from the heart. There are no wrong words here…"
              value={formData.message}
              onChange={(e) => updateFormData({ message: e.target.value.slice(0, 2000) })}
              className="min-h-[160px] text-base leading-relaxed resize-none"
            />
            <div className="text-right text-xs text-muted-foreground">
              {formData.message.length}/2000
            </div>
          </section>

          {/* Section 4: Photos */}
          <section className="glass rounded-2xl p-5 space-y-4 shadow-soft">
            <div className="flex items-center gap-2 text-primary mb-1">
              <Camera className="h-4 w-4" />
              <h2 className="font-heading text-base font-semibold">Add photos <span className="text-xs text-muted-foreground font-normal">(optional)</span></h2>
            </div>
            <div className="flex gap-3">
              {allPhotoUrls.map((url, i) => (
                <div key={i} className="relative w-20 h-20 rounded-xl overflow-hidden border-2 border-primary/20">
                  <img src={url} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" />
                  <button
                    onClick={() => removePhoto(i)}
                    className="absolute top-1 right-1 p-0.5 rounded-full bg-destructive text-destructive-foreground"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
              {allPhotoUrls.length < 3 && (
                <button type="button" onClick={openPhotoPicker} className="w-20 h-20 rounded-xl border-2 border-dashed border-muted-foreground/30 flex flex-col items-center justify-center cursor-pointer hover:border-primary/50 transition-colors">
                  <ImagePlus className="h-5 w-5 text-muted-foreground/50" />
                  <span className="text-[9px] text-muted-foreground mt-1">Add</span>
                </button>
              )}
            </div>
            <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleFileChange} className="hidden" />
          </section>

          {/* Mobile preview toggle */}
          {isMobile && (
            <button
              onClick={() => setShowPreview(!showPreview)}
              className="w-full glass rounded-2xl p-4 flex items-center justify-between shadow-soft"
            >
              <div className="flex items-center gap-2 text-primary">
                <Eye className="h-4 w-4" />
                <span className="font-heading text-base font-semibold">Live Preview</span>
              </div>
              <div className="flex items-center gap-2">
                {formData.message && (
                  <span className="text-[10px] text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                    {formData.message.trim().split(/\s+/).length} words
                  </span>
                )}
                {showPreview ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
              </div>
            </button>
          )}

          {/* Mobile inline preview with smooth expand */}
          <AnimatePresence>
            {isMobile && showPreview && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                className="overflow-hidden"
              >
                <LivePreviewCard
                  formData={formData}
                  existingPhotoUrls={existingPhotoUrls}
                  compact
                />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Floating mini preview bar on mobile (when preview is collapsed and user has content) */}
          <AnimatePresence>
            {isMobile && !showPreview && formData.message.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                className="fixed bottom-20 left-4 right-4 z-20"
              >
                <button
                  onClick={() => setShowPreview(true)}
                  className="w-full glass rounded-xl p-3 flex items-center gap-3 shadow-glass-lg border border-primary/20"
                >
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Eye className="h-4 w-4 text-primary" />
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <p className="text-xs font-medium text-foreground truncate">
                      {formData.recipientName ? `For ${formData.recipientName}` : 'Preview your message'}
                    </p>
                    <p className="text-[10px] text-muted-foreground truncate">
                      {formData.message.slice(0, 50)}{formData.message.length > 50 ? '…' : ''}
                    </p>
                  </div>
                  <ChevronUp className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Publish button */}
          <div className="pb-8">
            <Button
              onClick={handlePublish}
              disabled={isSubmitting || !formData.message.trim() || !formData.recipientName.trim() || !formData.relationship}
              className="w-full gap-2 h-12 text-base rounded-2xl shadow-soft"
              size="lg"
            >
              {isSubmitting ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Send className="h-5 w-5" />
              )}
              Publish Living Announcement
            </Button>
            <p className="text-xs text-muted-foreground text-center mt-2">
              Nothing is shared automatically. You control who sees this.
            </p>
          </div>
        </div>

        {/* RIGHT: Desktop sticky preview */}
        {!isMobile && (
          <div className="hidden lg:block lg:w-[400px] xl:w-[440px]">
            <div className="sticky top-20">
              <div className="flex items-center gap-2 text-muted-foreground mb-3">
                <Eye className="h-4 w-4" />
                <span className="text-sm font-medium">Live Preview</span>
              </div>
              <LivePreviewCard
                formData={formData}
                existingPhotoUrls={existingPhotoUrls}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
